import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CronJobLog {
  id: string;
  job_name: string;
  executed_at: string;
  records_affected: number;
  success: boolean;
  error_message: string | null;
}

const AdminCronLogs = () => {

  const { data: logs, isLoading } = useQuery({
    queryKey: ['cron-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cron_job_logs')
        .select('*')
        .order('executed_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as CronJobLog[];
    },
    refetchInterval: 60000, // Atualiza a cada minuto
  });

  const jobNames = [
    // Jobs de planos
    { name: 'expire_old_plan_changes', label: 'Expirar Planos Antigos', schedule: 'A cada hora (minuto 0)' },
    { name: 'activate_scheduled_plan_changes', label: 'Ativar Planos Agendados', schedule: 'A cada hora (minuto 5)' },
    { name: 'process_scheduled_plan_changes', label: 'Processar Mudanças Agendadas', schedule: 'A cada hora (minuto 10)' },
    { name: 'expire_user_plans', label: 'Expirar Planos de Usuários', schedule: 'A cada hora (minuto 15)' },
    // Jobs de email
    { name: 'process-scheduled-emails', label: '📧 Processar Emails', schedule: 'A cada 5 minutos' },
    { name: 'schedule-winback-emails', label: '📧 Agendar Winback', schedule: 'Diário 10:00 BRT' },
    { name: 'schedule-daily-digest', label: '📧 Digest Diário', schedule: 'Diário 19:00 BRT' },
    { name: 'schedule-subscription-alerts', label: '📧 Alertas Assinatura', schedule: 'Diário 07:00 BRT' },
    { name: 'cleanup-test-users', label: '🧹 Limpar Test Users', schedule: 'Diário 00:00 BRT' },
    // Jobs de sistema
    { name: 'update-offline-users', label: '🟢 Atualizar Offline', schedule: 'A cada 5 minutos' },
  ];

  const getJobStats = (jobName: string) => {
    const jobLogs = logs?.filter(log => log.job_name === jobName) || [];
    const lastExecution = jobLogs[0];
    const successCount = jobLogs.filter(log => log.success).length;
    const errorCount = jobLogs.filter(log => !log.success).length;
    const totalAffected = jobLogs.reduce((sum, log) => sum + (log.records_affected || 0), 0);

    return {
      lastExecution,
      successCount,
      errorCount,
      totalAffected,
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Monitoramento de Cron Jobs</h1>
        <p className="text-muted-foreground">
          Acompanhe a execução automática dos jobs de expiração de planos
        </p>
      </div>

      {/* Status dos Jobs */}
      <div className="grid gap-4 md:grid-cols-2">
        {jobNames.map(job => {
          const stats = getJobStats(job.name);
          return (
            <Card key={job.name} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{job.label}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{job.schedule}</p>
                </div>
                {stats.lastExecution?.success ? (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                ) : stats.lastExecution ? (
                  <XCircle className="h-6 w-6 text-red-500" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-500" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Última Execução</div>
                  <div className="font-medium">
                    {stats.lastExecution
                      ? format(new Date(stats.lastExecution.executed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                      : 'Nunca executado'}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Registros Afetados</div>
                  <div className="font-medium">{stats.lastExecution?.records_affected || 0}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Sucessos</div>
                  <div className="font-medium text-green-600">{stats.successCount}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Erros</div>
                  <div className="font-medium text-red-600">{stats.errorCount}</div>
                </div>
              </div>

              {stats.lastExecution?.error_message && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-mono">
                    {stats.lastExecution.error_message}
                  </p>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Histórico de Execuções */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Histórico de Execuções
        </h2>

        <div className="space-y-2">
          {logs?.slice(0, 50).map(log => (
            <div
              key={log.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-4 flex-1">
                {log.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                )}
                
                <div className="flex-1">
                  <div className="font-medium">
                    {jobNames.find(j => j.name === log.job_name)?.label || log.job_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {format(new Date(log.executed_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                  </div>
                  {log.error_message && (
                    <div className="text-sm text-red-600 mt-1 font-mono">
                      {log.error_message}
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <div className="text-sm font-medium">
                    {log.records_affected} {log.records_affected === 1 ? 'registro' : 'registros'}
                  </div>
                  <div className="text-xs text-muted-foreground">afetados</div>
                </div>
              </div>
            </div>
          ))}

          {!logs?.length && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma execução registrada ainda
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AdminCronLogs;
